import React from 'react'

import {Section, TextField, RadioField, CheckboxField} from 'jbc-front/components/Form'

import {
  reduxForm,
  formValueSelector,
  FormSection,
  FieldArray,
  autofill as autofillForm,
  isSubmitting as isSubmittingForm,
} from 'redux-form'
import Button from 'jbc-front/components/Button'
import FormErrors, {onSubmitFail, LabelMapper} from 'jbc-front/components/FormErrors'
import {useLocation} from 'react-router-dom'
import {pick, isEmpty, set} from 'lodash'
import {useSelector} from 'hooks/redux'
import {recordDisplay, toBooleanProps} from 'utils'
import {maxLength} from 'validators'
import EmployeeName from 'procedures/EmployeeName'
import {Consumer as AsyncTaskConsumer} from 'AsyncTask'
import ReviewComments from 'components/ReviewComments'
import {DateField} from 'FormFields'
import styles from './Form.scss'

export const makeDependentInitialValues = (dependent) =>
  pick(dependent, [
    'id',
    'first_name',
    'last_name',
    'relation_type',
    'dependent_social_insurance',
    'removed_on',
    'remove_reason_type',
    'remove_reason_detail',
    'died_on',
    'dependent_tax_law',
    'removed_tax_law_on',
    'remove_tax_law_reason_detail',
  ])

export const formName = 'changeDependents'
export const selector = formValueSelector(formName)
export const autofill = autofillForm.bind(null, formName)
export const isSubmitting = isSubmittingForm(formName)

const reasonTypesCommon = [
  {
    value: 'over_75',
    label: '75歳到達',
    note: '75歳になると「後期高齢者医療制度」に加入し、そこで被保険者となるため、健康保険の資格を喪失します。被扶養者異動日（資格喪失日）は75歳の誕生日です。',
  },
  {
    value: 'handicap',
    label: '障害認定',
    note: '家族が 65 歳～74 歳で一定の障害があると広域連合の障害認定を受けた方は「後期高齢者医療制度」の被保険者となるため、健康保険の資格を喪失します。',
  },
]

const reasonTypes = [
  {value: 'passed_away', label: '死亡'},
  {value: 'found_job', label: '就職'},
  {value: 'increase_in_income', label: '収入増加'},
  ...reasonTypesCommon,
  {value: 'others', label: 'その他'},
]

const reasonTypesKyokaiSpouse = [
  {value: 'passed_away', label: '死亡'},
  {value: 'divorce', label: '離婚'},
  {value: 'increase_in_income', label: '就職・収入増加'},
  ...reasonTypesCommon,
  {value: 'others', label: 'その他'},
]

const reasonTypesOthersSpouse = [
  {value: 'passed_away', label: '死亡'},
  {value: 'divorce', label: '離婚'},
  {value: 'increase_in_income', label: '収入増加'},
  ...reasonTypesCommon,
  {value: 'others', label: 'その他'},
]

const dependentOptions = [
  {value: 'true', label: '対象'},
  {value: 'false', label: '対象外'},
]

const reportTargetOptions = [
  {value: 'true', label: '記載する'},
  {value: 'false', label: '記載しない'},
]

const getReasonTypes = (healthInsuranceType, relation_type) => {
  if (relation_type === 'spouse') {
    return healthInsuranceType === 'kyokai' ? reasonTypesKyokaiSpouse : reasonTypesOthersSpouse
  } else {
    return reasonTypes
  }
}

const DateOn = () => (
  <Section title="被扶養者異動日">
    <DateField
      name="date_on"
      label="被扶養者でなくなった日"
      note="被扶養者が就職した時は「就職した日」（健康保険の資格取得日）を、離婚した時は「離婚日」を、死亡した時は「死亡日の翌日」を入力します。"
    />
  </Section>
)

const ItsForm = () => (
  <TextField name="remove_reason_detail" label="社会保険の扶養から外れた理由" validate={maxLength(24)} required />
)
const KyokaiForm = ({dependent, healthInsuranceType}) => (
  <>
    {
      <RadioField
        name="remove_reason_type"
        key="remove_reason_type"
        label="社会保険の扶養から外れた理由"
        options={getReasonTypes(healthInsuranceType, dependent.relation_type)}
        required
      />
    }
    {dependent.remove_reason_type === 'others' && (
      <TextField key="reason" name="remove_reason_detail" label="理由詳細" required validate={maxLength(24)} />
    )}
    {dependent.remove_reason_type === 'passed_away' && <DateField name="died_on" label="死亡日" required />}
  </>
)
const Dependent = ({name, index, healthInsuranceType, isEmployeeInput, isUpdate}) => {
  const dependent = useSelector((state) => selector(state, `dependents[${index}]`))
  const dependentSocialInsurance = dependent.dependent_social_insurance
  const dependentTaxLaw = dependent.dependent_tax_law

  // 従業員画面は、手続き前の状態が「対象外」の場合はラジオボタンを非活性にして対象に変更できないようにする
  const isSocialInsuranceDisabled = isEmployeeInput && dependent.initial_dependent_social_insurance === false
  const isTaxLawDisabled = isEmployeeInput && dependent.initial_dependent_tax_law === false

  return (
    <FormSection name={name}>
      <Section title={recordDisplay.fullName(dependent)}>
        <RadioField
          name="dependent_social_insurance"
          label="社会保険の扶養対象"
          {...toBooleanProps}
          options={dependentOptions}
          required
          disabled={!isUpdate && isSocialInsuranceDisabled}
          note={
            <p>
              被保険者により主として生計を維持されており、要件を満たす人が被扶養者となります。詳しくは
              <a
                href="https://jobcan-lms.zendesk.com/hc/ja/articles/360018787951-%E6%89%B6%E9%A4%8A%E3%81%A8%E3%81%AF"
                target="_blank"
                rel="noopener noreferrer"
              >
                こちら
              </a>
            </p>
          }
        />
        {!isEmployeeInput && (
          <RadioField
            name="is_report_target"
            label="扶養届への記載"
            {...toBooleanProps}
            options={reportTargetOptions}
            required
          />
        )}
        <>
          {!dependentSocialInsurance && (
            <>
              {!isEmployeeInput && (
                <DateField
                  name="removed_on"
                  label="社会保険の扶養から外れた日"
                  note="被扶養者が就職した時は「就職した日」（健康保険の資格取得日）を、離婚した時は「離婚日」を、死亡した時は「死亡日の翌日」を入力します。"
                  required
                />
              )}
              {healthInsuranceType === 'its' ? (
                <ItsForm />
              ) : (
                <KyokaiForm dependent={dependent} healthInsuranceType={healthInsuranceType} />
              )}
            </>
          )}
        </>

        <RadioField
          name="dependent_tax_law"
          label="税の扶養対象"
          {...toBooleanProps}
          options={dependentOptions}
          required
          disabled={!isUpdate && isTaxLawDisabled}
          note={
            <p>
              税法上の扶養は、その年の12月31日（納税者が年の中途で死亡し又は出国する場合は、その死亡又は出国の時）の現況で、要件を満たす人が扶養親族の対象となります。詳しくは
              <a
                href="https://jobcan-lms.zendesk.com/hc/ja/articles/360018787951-%E6%89%B6%E9%A4%8A%E3%81%A8%E3%81%AF"
                target="_blank"
                rel="noopener noreferrer"
              >
                こちら
              </a>
            </p>
          }
        />
        {!dependentTaxLaw && (
          <>
            {!isEmployeeInput && (
              <DateField
                name="removed_tax_law_on"
                label="税の扶養から外れた日"
                note="被扶養者が就職した時は「就職した日」（健康保険の資格取得日）を、離婚した時は「離婚日」を、死亡した時は「死亡日の翌日」を入力します。"
                required
              />
            )}
            <TextField
              name="remove_tax_law_reason_detail"
              label="税の扶養から外れた理由"
              validate={maxLength(24)}
              noteWidth={315}
              required
            />
          </>
        )}

        <div className={styles.section}>
          <CheckboxField
            name="removed"
            note="社会保険の扶養、税の扶養の両方とも対象外になっている家族しか削除できません。"
            label="従業員情報の「家族情報」から削除する"
          />
        </div>
      </Section>
    </FormSection>
  )
}

const renderDependents = ({fields, healthInsuranceType, isEmployeeInput}) => {
  return (
    <>
      {fields.length > 0 ? (
        fields.map((name, index) => (
          <Dependent
            {...{
              name,
              index,
              healthInsuranceType,
              isEmployeeInput,
            }}
            key={index}
          />
        ))
      ) : (
        <>該当従業員の家族情報がありません</>
      )}
    </>
  )
}

const validate = (values) => {
  const {dependents, date_on} = values
  if (!dependents) {
    return undefined
  }

  const errors = {dependents: []}

  const needDateOn = dependents.some(
    (dependent) => !dependent.dependent_social_insurance || !dependent.dependent_tax_law
  )
  if (needDateOn && !date_on) {
    errors.date_on = 'を入力してください'
  }

  dependents.forEach((dependent, index) => {
    if (dependent.is_report_target && dependent.dependent_social_insurance) {
      set(
        errors.dependents,
        `[${index}].is_report_target`,
        'は、社会保険の扶養対象外でないと記載することができません。'
      )
    }
    if (dependent.removed && (dependent.dependent_tax_law || dependent.dependent_social_insurance)) {
      set(
        errors.dependents,
        `[${index}].removed`,
        'は、社会保険の扶養、税の扶養の両方とも対象外になっている家族しか削除できません。'
      )
    }
  })

  return errors.date_on || Object.keys(errors.dependents).length > 0 ? errors : undefined
}

export const Form = reduxForm({
  form: formName,
  enableReinitialize: true,
  onSubmitFail,
  validate,
})(({submitting, handleSubmit, otherButtons, employee, healthInsuranceType, comments, submitText = '次へ'}) => {
  const {pathname} = useLocation()
  const isEmployeeInput = pathname.includes('/mypage/')
  const dependents = useSelector((state) => selector(state, 'dependents'))

  return (
    <form onSubmit={handleSubmit}>
      <div className="l-title-wrap">
        <h1 className="m-title-main">家族情報の削除</h1>
        <EmployeeName employee={employee} />
      </div>
      <div className="basic_information">
        <FormErrors />
        <ReviewComments comments={comments} />
        <DateOn />
        <Section title="家族情報">
          <LabelMapper name="dependents" label="家族情報の" />
          <FieldArray
            name="dependents"
            component={renderDependents}
            healthInsuranceType={healthInsuranceType}
            isEmployeeInput={isEmployeeInput}
          />
        </Section>
        <div className="u-ta-c u-mt30">
          {otherButtons}
          <AsyncTaskConsumer>
            {({taskRunningProps}) => (
              <Button primary onClick={handleSubmit} disabled={submitting || isEmpty(dependents)} {...taskRunningProps}>
                {submitText}
              </Button>
            )}
          </AsyncTaskConsumer>
        </div>
      </div>
    </form>
  )
})
